import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Lock, Globe, Fingerprint, ShieldCheck, FileText, Zap, Link2 } from "lucide-react";
import { ShieldScrollAnimation } from "@/components/ShieldScrollAnimation";
import { ServiceCard } from "@/components/ServiceCard";
import { AnimatedCounter } from "@/components/AnimatedCounter";
import { servicesData, trustMetrics } from "@/lib/servicesData";

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress: heroScroll } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroY = useTransform(heroScroll, [0, 1], [0, 200]);
  const heroOpacity = useTransform(heroScroll, [0, 0.8], [1, 0]);

  return (
    <div className="relative" data-testid="home-page">
      {/* ===== HERO ===== */}
      <section ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden">
        {/* Animated gradient background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[#020408]" />
          <motion.div
            animate={{
              background: [
                "radial-gradient(ellipse 80% 60% at 50% 40%, rgba(59,130,246,0.12) 0%, transparent 70%)",
                "radial-gradient(ellipse 80% 60% at 60% 50%, rgba(59,130,246,0.08) 0%, transparent 70%)",
                "radial-gradient(ellipse 80% 60% at 40% 35%, rgba(59,130,246,0.12) 0%, transparent 70%)",
              ],
            }}
            transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
            className="absolute inset-0"
          />
          {/* Grid pattern */}
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
              backgroundSize: "64px 64px",
            }}
          />
        </div>

        <motion.div
          style={{ y: heroY, opacity: heroOpacity }}
          className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-32 w-full"
        >
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left content */}
            <div>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 rounded-full px-4 py-1.5 mb-8"
              >
                <ShieldCheck className="w-4 h-4 text-blue-400" />
                <span className="text-blue-300 text-xs font-medium tracking-wide uppercase">
                  By the Propensiones International Group
                </span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="font-outfit font-bold text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.1] tracking-tight"
              >
                Legal & Technical{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-blue-600">
                  Certification
                </span>{" "}
                with International Standards
              </motion.h1>

              {/* TODO Add description */}
              {/* <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.5 }}
                className="mt-6 text-slate-400 text-base md:text-lg leading-relaxed max-w-lg"
              >
                Forensic certification, due diligence, and regulatory compliance backed by blockchain-signed NFT certificates and internationally recognized protocols.
              </motion.p> */}

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.7 }}
                className="mt-10 flex flex-col sm:flex-row gap-4"
              >
                <Link
                  to="/contact"
                  data-testid="hero-cta-request"
                  className="group relative bg-blue-600 hover:bg-blue-500 text-white font-medium px-8 py-3.5 rounded-md transition-all duration-300 hover:shadow-[0_0_30px_rgba(59,130,246,0.4)] hover:-translate-y-0.5 text-center"
                >
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    Request Service
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
                <Link
                  to="/services"
                  data-testid="hero-cta-services"
                  className="bg-transparent border border-slate-700 text-slate-300 hover:text-white hover:border-slate-500 font-medium px-8 py-3.5 rounded-md transition-all duration-300 text-center"
                >
                  View Services
                </Link>
              </motion.div>

              {/* Trust badges */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 1 }}
                className="mt-12 flex items-center gap-6 text-slate-600 text-xs"
              >
                <div className="flex items-center gap-2">
                  <Lock className="w-3.5 h-3.5" />
                  <span>256-bit Encryption</span>
                </div>
                <div className="flex items-center gap-2">
                  <Link2 className="w-3.5 h-3.5" />
                  <span>Blockchain-signed NFTs</span>
                </div>
                <div className="flex items-center gap-2">
                  <Globe className="w-3.5 h-3.5" />
                  <span>Global Recognition</span>
                </div>
              </motion.div>
            </div>

            {/* Right - Shield */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.4 }}
              className="hidden lg:flex justify-center items-center"
            >
              <ShieldScrollAnimation />
            </motion.div>
          </div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-5 h-8 border-2 border-slate-700 rounded-full flex justify-center"
          >
            <div className="w-1 h-2 bg-slate-500 rounded-full mt-1.5" />
          </motion.div>
        </motion.div>
      </section>

      {/* ===== SERVICES SECTION ===== */}
      <section className="relative py-24 md:py-32" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-blue-400 text-sm font-medium tracking-wider uppercase">Our Services</span>
            <h2 className="font-outfit font-bold text-3xl sm:text-4xl text-white mt-3">
              What We Certify
            </h2>
            <p className="mt-4 text-slate-400 max-w-2xl mx-auto text-base md:text-lg">
              Comprehensive certification services backed by blockchain-signed NFT certificates for regulatory compliance and legal protection across jurisdictions.
            </p>
          </motion.div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {servicesData.map((service, i) => (
              <ServiceCard key={service.slug} service={service} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ===== TRUST METRICS ===== */}
      <section className="relative py-24 md:py-32 overflow-hidden" data-testid="trust-section">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-950/5 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12">
            {trustMetrics.map((metric) => (
              <AnimatedCounter
                key={metric.label}
                value={metric.value}
                suffix={metric.suffix}
                label={metric.label}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ===== SECURITY SECTION ===== */}
      <section className="relative py-24 md:py-32" data-testid="security-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left - Visualization */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="relative aspect-square max-w-md mx-auto">
                {/* Background glow */}
                <div className="absolute inset-0 bg-blue-500/5 blur-3xl rounded-full" />

                {/* Network visualization */}
                <svg viewBox="0 0 400 400" className="w-full h-full relative z-10">
                  {/* Network connections */}
                  {[
                    { x1: 200, y1: 200, x2: 100, y2: 80 },
                    { x1: 200, y1: 200, x2: 300, y2: 80 },
                    { x1: 200, y1: 200, x2: 320, y2: 200 },
                    { x1: 200, y1: 200, x2: 300, y2: 320 },
                    { x1: 200, y1: 200, x2: 100, y2: 320 },
                    { x1: 200, y1: 200, x2: 80, y2: 200 },
                    { x1: 100, y1: 80, x2: 300, y2: 80 },
                    { x1: 300, y1: 80, x2: 320, y2: 200 },
                    { x1: 320, y1: 200, x2: 300, y2: 320 },
                    { x1: 300, y1: 320, x2: 100, y2: 320 },
                    { x1: 100, y1: 320, x2: 80, y2: 200 },
                    { x1: 80, y1: 200, x2: 100, y2: 80 },
                  ].map((line, i) => (
                    <motion.line
                      key={i}
                      x1={line.x1}
                      y1={line.y1}
                      x2={line.x2}
                      y2={line.y2}
                      stroke="rgba(59,130,246,0.15)"
                      strokeWidth="1"
                      initial={{ pathLength: 0 }}
                      whileInView={{ pathLength: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 1, delay: i * 0.1 }}
                    />
                  ))}

                  {/* Nodes */}
                  {[
                    { cx: 200, cy: 200, r: 20, label: "Core" },
                    { cx: 100, cy: 80, r: 8 },
                    { cx: 300, cy: 80, r: 8 },
                    { cx: 320, cy: 200, r: 8 },
                    { cx: 300, cy: 320, r: 8 },
                    { cx: 100, cy: 320, r: 8 },
                    { cx: 80, cy: 200, r: 8 },
                  ].map((node, i) => (
                    <motion.circle
                      key={i}
                      cx={node.cx}
                      cy={node.cy}
                      r={node.r}
                      fill={i === 0 ? "rgba(59,130,246,0.3)" : "rgba(59,130,246,0.15)"}
                      stroke="rgba(59,130,246,0.5)"
                      strokeWidth="1"
                      initial={{ scale: 0 }}
                      whileInView={{ scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: 0.5 + i * 0.1 }}
                    />
                  ))}

                  {/* Animated data flow pulses */}
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <motion.circle
                      key={`pulse-${i}`}
                      r="3"
                      fill="#3B82F6"
                      animate={{
                        cx: [200, [100, 300, 320, 300, 100, 80][i]],
                        cy: [200, [80, 80, 200, 320, 320, 200][i]],
                        opacity: [1, 0.3, 1],
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        delay: i * 0.5,
                        ease: "easeInOut",
                      }}
                    />
                  ))}

                  {/* Center shield icon */}
                  <text x="200" y="206" textAnchor="middle" fill="#F8FAFC" fontSize="20" fontFamily="sans-serif">
                    &#x1F6E1;
                  </text>
                </svg>

                {/* Document mockups */}
                {[
                  { x: "5%", y: "10%", rotate: -12 },
                  { x: "70%", y: "5%", rotate: 8 },
                  { x: "75%", y: "65%", rotate: 5 },
                ].map((pos, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.8 + i * 0.2 }}
                    className="absolute w-12 h-16 bg-slate-800/60 border border-slate-700/30 rounded-sm backdrop-blur-sm"
                    style={{ left: pos.x, top: pos.y, transform: `rotate(${pos.rotate}deg)` }}
                  >
                    <div className="w-full h-full p-1.5 flex flex-col gap-1">
                      <div className="w-full h-0.5 bg-slate-600/40 rounded" />
                      <div className="w-3/4 h-0.5 bg-slate-600/30 rounded" />
                      <div className="w-full h-0.5 bg-slate-600/20 rounded" />
                      <FileText className="w-3 h-3 text-blue-500/30 mt-auto mx-auto" />
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Right - Content */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <span className="text-blue-400 text-sm font-medium tracking-wider uppercase">Security, Blockchain & Compliance</span>
              <h2 className="font-outfit font-bold text-3xl sm:text-4xl text-white mt-3">
                NFT Certificates on Blockchain
              </h2>
              <p className="mt-6 text-slate-400 leading-relaxed">
                Every certificate we issue is minted as an NFT and signed on blockchain, creating an immutable, tamper-proof record of authenticity. Combined with military-grade encryption and internationally recognized compliance protocols, our infrastructure ensures unparalleled trust and traceability.
              </p>

              <div className="mt-8 space-y-4">
                {[
                  { icon: Link2, text: "Blockchain-signed NFT certificates with on-chain verification" },
                  { icon: Lock, text: "End-to-end 256-bit AES encryption" },
                  { icon: Fingerprint, text: "Immutable audit trails and tamper-proof records" },
                  { icon: Zap, text: "Real-time on-chain verification portal" },
                  { icon: Globe, text: "Multi-jurisdictional compliance framework" },
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 + i * 0.1 }}
                    className="flex items-center gap-4"
                  >
                    <div className="w-10 h-10 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center shrink-0">
                      <item.icon className="w-5 h-5 text-blue-400" />
                    </div>
                    <span className="text-slate-300 text-sm">{item.text}</span>
                  </motion.div>
                ))}
              </div>

              <Link
                to="/services"
                data-testid="security-cta"
                className="inline-flex items-center gap-2 mt-10 text-blue-400 hover:text-blue-300 font-medium text-sm transition-colors"
              >
                Explore Our Security Protocols
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== CTA SECTION ===== */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-950/20 via-transparent to-blue-950/20" />
        </div>
        <div className="relative max-w-3xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-outfit font-bold text-3xl sm:text-4xl lg:text-5xl text-white">
              Ready to Get Certified?
            </h2>
            <p className="mt-6 text-slate-400 text-base md:text-lg max-w-xl mx-auto">
              Start your certification process today. Our team of experts is ready to guide you through every step.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                data-testid="cta-bottom-request"
                className="bg-blue-600 hover:bg-blue-500 text-white font-medium px-10 py-3.5 rounded-md transition-all duration-300 hover:shadow-[0_0_30px_rgba(59,130,246,0.4)] hover:-translate-y-0.5"
              >
                Request Service
              </Link>
              <Link
                to="/services"
                data-testid="cta-bottom-services"
                className="border border-slate-700 text-slate-300 hover:text-white hover:border-slate-500 font-medium px-10 py-3.5 rounded-md transition-all duration-300"
              >
                View All Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
